import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    openAssociatedChatSession,
    toggleMeetingChatActive,
} from 'flow/actions/meeting-actions';
import { selectActiveMeetingViewIsFullscreen } from 'flow/selectors/meeting-selectors';
import useScreenSize from 'flow/hooks/useScreenSize';
import { ONE_PANE_WIDTH_LIMIT } from 'flow/utils/meeting-constants';

/**
 * Decides where the meeting chat opens, shared by the footer chat button and
 * the more-menu chat item (the footer collapses one into the other as it
 * narrows, so both need the same rule).
 *
 * The in-meeting chat panel takes over when the meeting owns the whole screen —
 * fullscreen, or a narrow window where the docked meeting goes full-width (the
 * communication canvas drops to w-screen at the one-pane limit) and the main
 * app view is hidden behind it. Only in side-pane + wide, where the main view
 * is visible beside the dock, do we navigate to its /messages chat — otherwise
 * the chat would open behind the meeting, out of sight.
 *
 * Driven off the live window width rather than the meeting's isNarrowWindow
 * flag, which is maintained by a hook inside MeetingVideoCanvas and can read
 * stale at these call sites.
 */
const useMeetingChatOpen = () => {
    const dispatch = useAppDispatch();
    const isFullscreenView = useAppSelector(
        selectActiveMeetingViewIsFullscreen,
    );
    const { width } = useScreenSize();

    const opensInMeetingChat =
        isFullscreenView || width <= ONE_PANE_WIDTH_LIMIT;

    const openChat = useCallback(() => {
        if (opensInMeetingChat) {
            dispatch(toggleMeetingChatActive());
        } else {
            dispatch(openAssociatedChatSession());
        }
    }, [dispatch, opensInMeetingChat]);

    return { openChat, opensInMeetingChat };
};

export default useMeetingChatOpen;
